/**
 * src/api/activityLogger.js
 *
 * Lightweight activity logging layer backed by the activity-log Azure Function.
 * Writes are fire-and-forget — they never block the UI or throw to callers.
 *
 * action_type values:
 *   Generic: 'login' | 'logout' | 'created' | 'edited' | 'deleted'
 *   Risk governance (Phase 2a — vocabulary reserved, no callers yet):
 *     'risk-state-transition' | 'risk-assertion-recorded' |
 *     'risk-decision-recorded' | 'risk-evidence-recorded' |
 *     'risk-obligation-missing' | 'risk-reopened' | 'risk-retired'
 *
 * Values are free-form strings at the transport level; the enum above is
 * documentation, not enforcement. Consumers filter by exact string match.
 */

import { azurePost, azureGet, azureFetch, azureEnvPost, azureEnvGet } from './azureClient.js';

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Log a single activity event. Safe to call anywhere — never throws, never blocks.
 *
 * @param {string} tenantId
 * @param {{ userId, displayName, username, role }} session
 * @param {{ module, actionType, recordId?, recordLabel? }} event
 */
export function logActivity(tenantId, session, event) {
  if (!tenantId || !session?.userId) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- intentional dev-only diagnostic
      console.debug('[activityLogger] skipped — missing tenantId or session.userId', {
        tenantId,
        userId: session?.userId,
      });
    }
    return;
  }

  // For super-admin sessions (no tenantId on the JWT), pass tenantId in the
  // body so the Azure Function can route the insert to the correct tenant.
  const isSuperAdmin = session.role === 'super-admin';

  // Fire-and-forget POST to activity-log Azure Function
  azurePost('/api/activity-log', {
    ...(isSuperAdmin ? { tenantId } : {}),
    userId: session.userId,
    userName: session.displayName || session.username || 'Unknown',
    userRole: session.role || 'reader',
    module: event.module,
    actionType: event.actionType,
    recordId: event.recordId ?? null,
    recordLabel: event.recordLabel ?? null,
  }).catch((err) => {
    console.warn('[activityLogger] azure insert failed:', err.message);
  });
}

/**
 * Env-aware variant of logActivity for the super-admin portal — routes the
 * write to the correct Function App (production / test / development).
 */
export function logActivityForEnv(env, tenantId, session, event) {
  if (!tenantId || !session?.userId) return;
  azureEnvPost(env, '/api/activity-log', {
    tenantId,
    userId: session.userId,
    userName: session.displayName || session.username || 'Unknown',
    userRole: session.role || 'super-admin',
    module: event.module,
    actionType: event.actionType,
    recordId: event.recordId ?? null,
    recordLabel: event.recordLabel ?? null,
  }).catch((err) => {
    console.warn('[activityLogger] env insert failed:', err.message);
  });
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Fetch activity log entries for a tenant within the retention window.
 * @param {string} tenantId
 * @param {number} retentionDays  — only rows newer than this many days
 * @param {number} limit          — max rows (default 200)
 * @param {{ userId?: string, userRole?: string, module?: string, actionType?: string, search?: string, recordId?: string, env?: string }} [filters]
 *   — `env` routes the read to another environment's Function App for the
 *   super-admin portal, and names the tenant explicitly (a super-admin JWT
 *   carries no tenant_id).
 * @param {{ swallowErrors?: boolean }} [options]
 *   — GH #6236. This function THROWS on a failed read by default, deliberately.
 *   A caller that GATES on the answer ("does this record have history?") must
 *   never be handed [] for "we could not find out": the employee hard-delete
 *   dialog did exactly that, and offered to permanently destroy acknowledgment
 *   evidence whenever this query timed out. Safety is the default here rather
 *   than something a caller has to remember to ask for — a gate must return the
 *   restrictive answer for anything unrecognised, never opt into it.
 *   Pass `{ swallowErrors: true }` ONLY from a read-only DISPLAY surface, where
 *   an empty list is an honest rendering of "nothing to show right now".
 */
export async function readActivityLog(
  tenantId,
  retentionDays = 90,
  limit = 200,
  filters = {},
  options = {}
) {
  try {
    const params = { retentionDays, limit };
    if (filters.userId) params.userId = filters.userId;
    if (filters.module) params.module = filters.module;
    if (filters.actionType) params.actionType = filters.actionType;
    if (filters.search) params.search = filters.search;
    if (filters.recordId) params.recordId = filters.recordId;
    if (filters.env) params.tenantId = tenantId;
    const data = filters.env
      ? await azureEnvGet(filters.env, '/api/activity-log', params)
      : await azureGet('/api/activity-log', params);
    return data.entries ?? [];
  } catch (e) {
    console.error('[activityLogger] readActivityLog failed:', e.message);
    // Default is to THROW — see the options JSDoc above. Returning [] to every
    // caller is what made a failed read indistinguishable from a clean history
    // (GH #6236).
    if (!options.swallowErrors) throw e;
    return [];
  }
}

/**
 * Count rows OLDER than retentionDays for a tenant.
 * Azure doesn't expose a separate count endpoint — return 0 as a safe fallback;
 * the prune will still work correctly.
 */
export function countOldActivityRows(_tenantId, _retentionDays) {
  return 0;
}

/**
 * Delete rows older than retentionDays for a tenant.
 * Called only after explicit admin confirmation.
 */
export async function pruneActivityLog(_tenantId, retentionDays) {
  const res = await azureFetch(`/api/activity-log?retentionDays=${retentionDays}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Activity log prune failed: ${res.status}`);
  }
}
